// src/components/modals/AdoptionPostDetailModal.jsx
"use client";

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { adoptionAPI } from "../../services/adoptionAPI";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import { handleApiError } from "../../services/api";
import { getBreedName } from "../../utils/petDataUtils";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import MultiMediaDisplay from "../social/posts/MultiMediaDisplay";
import AdoptionOfferConfirmationModal from "./AdoptionOfferConfirmationModal";
import {
  showSuccessToast,
  showErrorToast,
} from "../../utils/notificationUtils";
import "./AdoptionPostDetailModal.css";

const AdoptionPostDetailModal = ({
  isOpen,
  onClose,
  postId,
  post: initialPost = null,
  currentUser: propCurrentUser = null,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const { user: authUser, getUserType } = useAuth();

  // Use prop currentUser if provided, otherwise fallback to auth hook user
  const currentUser = propCurrentUser || authUser;

  // Accent color driven by viewer's user type (modal is portal-mounted, so it
  // can't inherit --adoption-accent from the adoption page root)
  const userTypeColor = getUserTypeColor(getUserType());

  const [post, setPost] = useState(initialPost);
  const [loading, setLoading] = useState(!initialPost && !!postId);
  const [error, setError] = useState(null);
  const [isOffering, setIsOffering] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [activePetIndex, setActivePetIndex] = useState(0);
  const [showOfferConfirmation, setShowOfferConfirmation] = useState(false);
  const scrollLockRef = useRef(0);

  // Fetch post from API if we only have an id
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setError(null);

    if (initialPost) {
      setPost(initialPost);
      setLoading(false);
      return;
    }

    if (!postId) return;

    let cancelled = false;

    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await adoptionAPI.getAdoptionPost(postId);

        let data = null;
        if (response?.data) {
          if (response.data.status === "success" && response.data.data) {
            data = response.data.data;
          } else if (response.data.data) {
            data = response.data.data;
          } else {
            data = response.data;
          }
        }

        if (!cancelled) {
          setPost(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(handleApiError(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPost();

    return () => {
      cancelled = true;
    };
  }, [isOpen, postId, initialPost]);

  // Handle ESC + body scroll lock + preserve scroll position
  // Using the same pattern as other adoption modals (CreateAdoptionPostModal, etc.)
  useEffect(() => {
    if (!isOpen) {
      // Restore scroll position when modal closes
      if (scrollLockRef.current) {
        window.scrollTo({ top: scrollLockRef.current, behavior: "instant" });
        scrollLockRef.current = 0;
      }
      return;
    }

    // Save scroll position IMMEDIATELY when modal opens
    scrollLockRef.current =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      0;

    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !loading) {
        onClose();
      }
    };

    // Lock body scroll using the same pattern as other adoption modals
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollLockRef.current}px`;
    document.body.style.width = "100%";

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      // Restore body styles
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";

      // Restore scroll position
      if (scrollLockRef.current) {
        window.scrollTo({ top: scrollLockRef.current, behavior: "instant" });
      }

      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, loading, onClose]);

  // Reset active pet index when post changes or modal opens
  useEffect(() => {
    if (!isOpen || !post) return;

    // Derive pet info to check if we have pets
    const petInfo = post?.pet_info
      ? Array.isArray(post.pet_info)
        ? post.pet_info
        : [post.pet_info]
      : Array.isArray(post?.pets)
        ? post.pets
        : post?.pet
          ? [post.pet]
          : [];

    if (petInfo.length > 0) {
      setActivePetIndex(0);
    }
  }, [isOpen, post?.id]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  // Derive pet info
  const petInfo = post?.pet_info
    ? Array.isArray(post.pet_info)
      ? post.pet_info
      : [post.pet_info]
    : Array.isArray(post?.pets)
      ? post.pets
      : post?.pet
        ? [post.pet]
        : [];

  const hasMultiplePets = petInfo.length > 1;
  const activePet = petInfo[activePetIndex] || petInfo[0] || {};

  // Build media list for the active pet: primary profile image first, then additional images
  const getMediaForPet = (pet) => {
    const media = [];
    const primaryImage =
      pet.picture ||
      pet.profile_image_url ||
      (typeof pet.profile_image_path === "string"
        ? pet.profile_image_path
        : pet.profile_image_path?.url || pet.profile_image_path?.path) ||
      pet.image ||
      pet.avatar;

    if (primaryImage) {
      media.push(primaryImage);
    }

    // Add additional images if they exist
    // Note: additional_images are shared at the post level, so we show them for all pets
    if (Array.isArray(post?.additional_images)) {
      post.additional_images.forEach((img) => {
        const url =
          typeof img === "string" ? img : img?.url || img?.path || null;
        if (url && url !== primaryImage) {
          media.push(url);
        }
      });
    }

    return media;
  };

  const media = getMediaForPet(activePet);

  // Helper function to get pet details
  const getPetDetails = (pet) => {
    const ageLabel = (() => {
      const raw = pet.birthdate || pet.birth_date;
      if (!raw) return t("adoption.post.pet.notSpecified") || "Not specified";
      const birth = new Date(raw);
      const today = new Date();
      let years = today.getFullYear() - birth.getFullYear();
      let months = today.getMonth() - birth.getMonth();
      if (months < 0 || (months === 0 && today.getDate() < birth.getDate())) {
        years--;
        months += 12;
      }
      if (years <= 0) {
        return `${months} ${t("adoption.post.pet.months") || "months"}`;
      }
      if (months === 0) {
        return `${years} ${t("adoption.post.pet.years") || "years"}`;
      }
      return `${years}y ${months}m`;
    })();

    const gender =
      pet.gender || t("adoption.post.pet.notSpecified") || "Not specified";
    const breed =
      getBreedName(pet.breed) ||
      t("adoption.post.pet.notSpecified") ||
      "Not specified";
    const weight = pet.weight_kg || pet.weight_kg_fmt || pet.weight;

    const neuteredFlag =
      pet.is_neutered ?? pet.neutered ?? pet.spayed_neutered ?? null;
    const vaccinatedFlag =
      post?.vaccinated ?? pet.is_vaccinated ?? pet.vaccinated ?? null;

    const neuteredLabel =
      neuteredFlag === null
        ? t("adoption.post.pet.notSpecified") || "Not specified"
        : neuteredFlag
          ? t("common.yes") || "Yes"
          : t("common.no") || "No";

    const vaccinatedLabel =
      vaccinatedFlag === null
        ? t("adoption.post.pet.notSpecified") || "Not specified"
        : vaccinatedFlag
          ? t("common.yes") || "Yes"
          : t("common.no") || "No";

    return {
      ageLabel,
      gender,
      breed,
      weight,
      neuteredFlag,
      vaccinatedFlag,
      neuteredLabel,
      vaccinatedLabel,
    };
  };

  const petDetails = getPetDetails(activePet);

  // Get the adoption post owner ID from multiple possible locations
  // Note: post.author is the social post author (sharer), not the adoption post owner
  // Check nested adoption_post structure first, then direct post structure
  const adoptionPostOwnerId =
    post?.adoption_post?.author?.id ||
    post?.adoption_post?.author?.user_id ||
    post?.adoption_post?.user_id ||
    post?.adoption_post?.user?.id ||
    post?.adoption_post?.owner_id ||
    // Also check direct post structure (when viewing own post directly)
    post?.author?.id ||
    post?.author?.user_id ||
    post?.user_id ||
    post?.user?.id ||
    post?.owner_id ||
    null;

  // Get current user ID from multiple possible locations
  const currentUserId = currentUser?.id || currentUser?.user_id || null;

  // Compare against adoption post owner (not the social post sharer)
  // Use strict comparison with string conversion to handle number/string mismatches
  const isOwner =
    currentUserId &&
    adoptionPostOwnerId &&
    String(currentUserId) === String(adoptionPostOwnerId);

  const isAdopted = (post?.status || "").toLowerCase() === "adopted";
  const myRequestStatus = post?.my_request_status || null;

  const handleOfferToAdopt = async () => {
    if (!post?.id || isOffering) return;
    setIsOffering(true);
    try {
      const response = await adoptionAPI.sendAdoptionRequest(post.id);

      // Get the new request ID from response if available
      const newRequestId = response?.data?.data?.id || response?.data?.id;

      showSuccessToast(
        t("adoption.postFeed.offerSuccess") ||
          "Your offer to adopt has been sent successfully!",
      );
      setShowOfferConfirmation(true);
      // Mark as pending locally so button reflects new state
      setPost((prev) =>
        prev
          ? {
              ...prev,
              my_request_status: "pending",
              my_request_id: newRequestId || prev.my_request_id,
              adoption_requests_count: (prev.adoption_requests_count || 0) + 1,
            }
          : prev,
      );
    } catch (err) {
      const message =
        handleApiError(err) ||
        t("adoption.postFeed.offerError") ||
        "Failed to send adoption offer";
      showErrorToast(message);
    } finally {
      setIsOffering(false);
    }
  };

  const handleCancelAdoptionRequest = async () => {
    const requestId = post?.my_request_id;
    if (!requestId || isCancelling) return;

    setIsCancelling(true);
    try {
      await adoptionAPI.cancelAdoptionRequest(requestId);
      showSuccessToast(
        t("adoption.postFeed.cancelSuccess") ||
          "Your adoption request has been cancelled successfully!",
      );
      // Reset status locally
      setPost((prev) =>
        prev
          ? {
              ...prev,
              my_request_status: null,
              my_request_id: null,
              adoption_requests_count: Math.max(
                0,
                (prev.adoption_requests_count || 0) - 1,
              ),
            }
          : prev,
      );
    } catch (err) {
      const message =
        handleApiError(err) ||
        t("adoption.postFeed.cancelError") ||
        "Failed to cancel adoption request";
      showErrorToast(message);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleViewProfile = (e) => {
    if (!adoptionPostOwnerId) return;
    e?.preventDefault();
    e?.stopPropagation();
    // Open profile in a new tab to avoid navigating away from current page
    const profileUrl = `/social/profile/${adoptionPostOwnerId}`;
    window.open(profileUrl, "_blank", "noopener,noreferrer");
  };

  const createdAt = post?.created_at || post?.createdAt || null;
  const createdAtLabel = createdAt
    ? new Date(createdAt).toLocaleDateString(
        i18n.language === "ar" ? "ar-SA" : "en-US",
        { year: "numeric", month: "long", day: "numeric" },
      )
    : null;

  // Get owner name from adoption post (not the social post sharer)
  const ownerName =
    post?.adoption_post?.author?.name ||
    post?.adoption_post?.author?.username ||
    post?.adoption_post?.user?.name ||
    post?.adoption_post?.user?.username ||
    post?.author?.name ||
    post?.author?.username ||
    "";

  const title = hasMultiplePets
    ? t("adoption.details.multiplePetsTitle", { count: petInfo.length }) ||
      `${petInfo.length} Pets for Adoption`
    : ownerName
      ? t("adoption.details.titleWithName", { name: activePet.name || "" }) ||
        `${activePet.name || t("adoption.page.title")}`
      : activePet.name || t("adoption.page.title") || "Pet adoption";

  const content = (
    <>
      <div
        className="adoption-post-detail-modal-overlay"
        onClick={handleBackdropClick}
        style={{ "--adoption-accent": userTypeColor }}
      >
        <div
          className="adoption-post-detail-modal"
          onClick={(e) => e.stopPropagation()}
          dir={isRTL ? "rtl" : "ltr"}
        >
          <div className="adoption-post-detail-modal-header">
            <div className="adoption-post-detail-modal-header-text">
              <h2 className="adoption-post-detail-modal-title">{title}</h2>
              {createdAtLabel && (
                <span className="adoption-post-detail-modal-date">
                  {createdAtLabel}
                </span>
              )}
            </div>
            <button
              className="adoption-post-detail-modal-close"
              onClick={onClose}
              disabled={loading}
            >
              ×
            </button>
          </div>

          <div className="adoption-post-detail-modal-content">
            {loading ? (
              <div className="adoption-post-detail-modal-loading">
                <div className="adoption-post-detail-modal-spinner" />
                <p>{t("adoption.requests.loading") || "Loading post..."}</p>
              </div>
            ) : error ? (
              <div className="adoption-post-detail-modal-error">
                <p>{error}</p>
                {postId && (
                  <button onClick={() => setPost(null)}>
                    {t("adoption.requests.retry") || "Retry"}
                  </button>
                )}
              </div>
            ) : !post ? (
              <div className="adoption-post-detail-modal-empty">
                <p>{t("adoption.requests.empty") || "Post not found."}</p>
              </div>
            ) : (
              <div className="adoption-detail-layout">
                <div
                  className="adoption-detail-media"
                  key={`pet-media-${activePetIndex}`}
                >
                  {media.length > 0 ? (
                    // Reuse the same gallery component as social posts
                    <MultiMediaDisplay media={media} />
                  ) : (
                    <div className="adoption-detail-media-placeholder">
                      <img
                        src="/images/no-image-pet.png"
                        alt={activePet.name || "Pet"}
                      />
                    </div>
                  )}
                </div>

                <div className="adoption-detail-info">
                  {/* Owner */}
                  <div className="adoption-detail-owner">
                    <div className="adoption-detail-owner-main">
                      <div className="adoption-detail-owner-avatar">
                        <img
                          src={
                            post?.adoption_post?.author?.avatar ||
                            post?.adoption_post?.author?.profile_image_url ||
                            post?.adoption_post?.user?.avatar ||
                            post?.adoption_post?.user?.profile_image_url ||
                            post?.author?.avatar ||
                            post?.author?.profile_image_url ||
                            getUserPlaceholderImage(
                              post?.adoption_post?.author || post?.author,
                            )
                          }
                          alt={ownerName || "Owner"}
                          onError={(e) => {
                            e.target.src = getUserPlaceholderImage(
                              post?.adoption_post?.author || post?.author,
                            );
                          }}
                        />
                      </div>
                      <div className="adoption-detail-owner-text">
                        <span className="adoption-detail-owner-label">
                          {t("adoption.post.owner") || "Owner"}
                        </span>
                        <span className="adoption-detail-owner-name">
                          {ownerName}
                        </span>
                      </div>
                    </div>
                    {adoptionPostOwnerId && (
                      <a
                        href={`/social/profile/${adoptionPostOwnerId}`}
                        className="adoption-detail-owner-button"
                        onClick={handleViewProfile}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("adoption.post.viewProfile") || "View profile"}
                      </a>
                    )}
                  </div>

                  {/* Pet tabs (if multiple pets) */}
                  {hasMultiplePets && (
                    <div className="adoption-detail-pet-tabs">
                      {petInfo.map((pet, index) => (
                        <button
                          key={pet.id || index}
                          type="button"
                          className={`adoption-detail-pet-tab ${
                            activePetIndex === index ? "active" : ""
                          }`}
                          onClick={() => setActivePetIndex(index)}
                        >
                          {pet.name ||
                            t("adoption.modals.selectPet.unknownPet") ||
                            `Pet ${index + 1}`}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Pet details */}
                  <div className="adoption-detail-section">
                    <h3 className="adoption-detail-section-title">
                      {activePet.name ||
                        t("adoption.post.pet.details") ||
                        "Pet details"}
                    </h3>
                    <div className="adoption-detail-grid">
                      <div className="adoption-detail-item">
                        <div className="adoption-detail-item-label">
                          {t("adoption.post.pet.age") || "Age"}
                        </div>
                        <div className="adoption-detail-item-value">
                          {petDetails.ageLabel}
                        </div>
                      </div>
                      <div className="adoption-detail-item">
                        <div className="adoption-detail-item-label">
                          {t("adoption.post.pet.gender") || "Gender"}
                        </div>
                        <div className="adoption-detail-item-value">
                          {petDetails.gender}
                        </div>
                      </div>
                      <div className="adoption-detail-item">
                        <div className="adoption-detail-item-label">
                          {t("adoption.post.pet.breed") || "Breed"}
                        </div>
                        <div className="adoption-detail-item-value">
                          {petDetails.breed}
                        </div>
                      </div>
                      <div className="adoption-detail-item">
                        <div className="adoption-detail-item-label">
                          {t("adoption.post.pet.weight") || "Weight"}
                        </div>
                        <div className="adoption-detail-item-value">
                          {petDetails.weight
                            ? `${petDetails.weight} kg`
                            : t("adoption.post.pet.notSpecified") ||
                              "Not specified"}
                        </div>
                      </div>
                      <div className="adoption-detail-item">
                        <div className="adoption-detail-item-label">
                          {t("adoption.post.pet.neutered") ||
                            "Neutered / spayed"}
                        </div>
                        <span
                          className={`adoption-detail-pill ${
                            petDetails.neuteredFlag
                              ? "adoption-detail-pill--yes"
                              : petDetails.neuteredFlag === false
                                ? "adoption-detail-pill--no"
                                : ""
                          }`}
                        >
                          {petDetails.neuteredLabel}
                        </span>
                      </div>
                      <div className="adoption-detail-item">
                        <div className="adoption-detail-item-label">
                          {t("adoption.post.pet.vaccinated") || "Vaccinated"}
                        </div>
                        <span
                          className={`adoption-detail-pill ${
                            petDetails.vaccinatedFlag
                              ? "adoption-detail-pill--yes"
                              : petDetails.vaccinatedFlag === false
                                ? "adoption-detail-pill--no"
                                : ""
                          }`}
                        >
                          {petDetails.vaccinatedLabel}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Adoption reasons / Adoption terms */}
                  {post?.about_pet && (
                    <div className="adoption-detail-section">
                      <h4 className="adoption-detail-section-title">
                        {t("adoption.post.about") || "Adoption reasons"}
                      </h4>
                      <p className="adoption-detail-text">{post.about_pet}</p>
                    </div>
                  )}

                  {post?.looking_for && (
                    <div className="adoption-detail-section">
                      <h4 className="adoption-detail-section-title">
                        {t("adoption.post.lookingFor") || "Adoption terms"}
                      </h4>
                      <p className="adoption-detail-text">{post.looking_for}</p>
                    </div>
                  )}

                  {!isOwner && !isAdopted && (
                    <div className="adoption-detail-actions">
                      <button
                        type="button"
                        className="adoption-detail-offer-btn"
                        onClick={handleOfferToAdopt}
                        disabled={isOffering || myRequestStatus === "pending"}
                      >
                        {isOffering
                          ? t("adoption.post.offerSubmitting") ||
                            "Submitting..."
                          : myRequestStatus === "pending"
                            ? t("adoption.post.pending") || "Pending"
                            : t("adoption.post.offerToAdopt") ||
                              "Offer to Adopt"}
                      </button>

                      {myRequestStatus === "pending" && (
                        <button
                          type="button"
                          className="adoption-detail-cancel-btn"
                          onClick={handleCancelAdoptionRequest}
                          disabled={isCancelling}
                        >
                          {isCancelling
                            ? t("adoption.requests.cancelling") ||
                              "Cancelling..."
                            : t("adoption.requests.cancel") || "Cancel Request"}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Safety disclaimer - spans entire width */}
                <div className="adoption-detail-section adoption-detail-disclaimer">
                  <h4 className="adoption-detail-section-title">
                    {t("adoption.details.disclaimerTitle") ||
                      "Safety disclaimer"}
                  </h4>
                  <p className="adoption-detail-text adoption-detail-disclaimer-text">
                    {t("adoption.details.disclaimerBody") ||
                      "Sulhafa is a community platform and does not independently verify the identity, information, or intentions of users. Always proceed with caution when arranging adoption offers, independently confirm all details about the pet and the other party, and avoid sharing sensitive personal or financial information."}
                  </p>
                </div>

                {/* Footer - spans entire width */}
                {post?.adoption_requests_count > 0 && (
                  <div className="adoption-detail-footer">
                    <div className="adoption-detail-footer-meta">
                      <span className="adoption-detail-requests">
                        {post.adoption_requests_count}{" "}
                        {t("adoption.post.requests") || "request(s)"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <AdoptionOfferConfirmationModal
        isOpen={showOfferConfirmation}
        onClose={() => setShowOfferConfirmation(false)}
        authorName={ownerName}
      />
    </>
  );

  return createPortal(content, document.body);
};

export default AdoptionPostDetailModal;
